import UserBankAccounts from "./UserBankAccounts";
import { useTranslation } from "react-i18next";
import { Col } from "react-bootstrap";
import { Helmet } from "react-helmet";
import Tabs, { Tab } from "react-best-tabs";
import "react-best-tabs/dist/index.css";
import CustomTable from "../../components/DataTable/CustomTable";
import TableHeader from "../../components/DataTable/TableHeader";
import { UserPlus } from "lucide-react";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CashDepositwithdraw from "./CashDepositwithdraw";
import CashVoucher from "./CashVoucher";
import { getallbanktobankTransaction } from "../../services/authapi";
import { useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { serialNumber } from "../../utils/helper";
import ActionButtons from "../../components/DataTable/ActionButtons";
import { formatNumberToINR } from "../../utils/helper";
import { AccountDetailCard } from "../../components/ItemDetail";
import { deleteBanktoBank } from "../../services/authapi";
import { toast } from "react-toastify";
import ConfirmAlert from "../../components/ConfirmAlert";
import { useInnerTabGate, useInnerTabState, useFlagGate } from "../../utils/menuNodes";

const Transaction = ({ checkPermission }) => {
  // Which tabs inside this one may this role open (utils/menuNodes.js).
  // Fails open: an inner tab with no permission row stays visible.
  const canSeeInner = useInnerTabGate("/bank");
  // Each sub-tab's Create button reads ITS row's Add (2026-09-21); the tab's
  // own Add is the fallback where a sub-tab row is missing.
  const hasFlag = useFlagGate("/bank");
    const columnHelper = createColumnHelper();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const { userPermission } = useSelector(selectUser);
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [activeTab, setActiveTab] = useState(
        localStorage.getItem("last_tab") || "2"
    );
  // Pills the role may not see are hidden (2026-09-18); the state jumps to
  // the first visible tab when it points at a hidden one.
  const inner = useInnerTabState("/bank", { 2: "bank-to-bank-transfer", 3: "bank-cash-withdraw-deposit", 4: "bank-cash-voucher" }, activeTab, setActiveTab);
    const pageNo = searchParams.get("pageNo") || 1;
    const pageSize = searchParams.get("pageSize") || 10;
    const [search, setSearch] = useState("");
    const user = useSelector(selectUser);
    const user_id = useSelector((state) => state.auth.user.id);
    const user_type = useSelector((state) => state.auth.user.user_type);
    const [idToDelete, setIdToDelete] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [transactionType, setTransactiontype] = useState("");

    const handleClick = (e, tab) => {
        setActiveTab(tab);
        if (searchParams.has("pageNo")) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("pageNo");
            navigate({
                pathname: location.pathname,
                search: newParams.toString(),
            });
        }
        localStorage.setItem("last_tab", tab);
    };

    const columns = [
        columnHelper.accessor("sr_no", {
            header: t("Sr No."),
            cell: (info) => {
                const serialNumbers = serialNumber(pageNo, pageSize);
                return serialNumbers[info.row.index];
            },
        }),
        {
            header: "Transaction Category",
            accessorKey: "txn_category",
            cell: ({ row }) => {
                const category = row.original.txn_category;

                switch (category) {
                    case "BB":
                        return "Bank to Bank";
                    case "BU":
                        return "Bank to User";
                    case "UU":
                        return "User to User";
                    case "BC":
                        return "Bank to Creditor";
                    case "UB":
                        return "User to Bank";
                    case "CB":
                        return "Creditor to Bank";
                    default:
                        return category || "User to User";
                }
            },
        },
        {
            header: "Transaction Date",
            accessorFn: (row) => {
                const rawDate = row.bank_txn_date || row.user_txn_date;
                if (!rawDate) return "-";

                const date = new Date(rawDate);
                return date.toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                });
            },
        },
        {
            header: "From Account Details",
            accessorKey: "from_bank_account_holder_name", // placeholder, main accessorFn use karenge
            cell: ({ row }) => {
                const data = row.original;

                // Bank transaction
                if (data.from_bank_account_holder_name) {
                    return (
                        <AccountDetailCard
                            name={data.from_bank_account_holder_name}
                            accountNumber={data.from_bank_account_number}
                            bankName={`${data.from_bank_name} | Balance: ₹${Number(data.from_bank_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                }`}
                        />
                    );
                }

                // User-to-User transaction
                if (data.from_user_name) {
                    return (
                        <AccountDetailCard
                            name={data.from_user_name}
                            accountNumber={data.from_user_account_number}
                            bankName={`${data.from_user_bank_name} | Balance: ₹${Number(data.from_user_bank_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                }`}
                        />
                    );
                }

                return "-"; // default fallback
            },
        },

        {
            header: "To Account Details",
            accessorKey: "to_account_details", // placeholder
            cell: ({ row }) => {
                const data = row.original;

                // Bank to Bank / Bank to Creditor
                if (data.to_bank_account_holder_name) {
                    return (
                        <AccountDetailCard
                            name={data.to_bank_account_holder_name}
                            accountNumber={data.to_bank_account_number}
                            bankName={`${data.to_bank_name} | Balance: ₹ ${Number(data.to_bank_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        />
                    );
                }

                // Bank to User / User to User
                if (data.to_user_name) {
                    return (
                        <AccountDetailCard
                            name={data.to_user_name}
                            accountNumber={data.to_user_account_number}
                            bankName={`${data.to_user_bank_name} | Balance: ₹${Number(data.to_user_balance || data.to_user_bank_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                }`}
                        />
                    );
                }

                return "-";
            },
        },


        {
            header: "Transaction ID",
            accessorFn: (row) => row.bank_txn_id || row.transaction_id || "-",
        },
        {
            header: "Amount",
            accessorFn: (row) => row.bank_amount || row.user_amount || "-",
            cell: ({ row }) => {
                const amount = row.original.bank_amount || row.original.user_amount;
                return amount !== undefined ? (
                    <span style={{ color: "green" }} className="fw-bold">
                        ₹ {Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                ) : (
                    "-"
                );
            },
        },

        {
            header: "Payment Mode",
            accessorFn: (row) => row.payment_mode || "-",
        },
        {
            header: "Status",
            accessorFn: (row) => row.bank_txn_status || row.user_txn_status || "-",
        },

        {
            header: "Remark",
            accessorFn: (row) => row.remark || "-",
        },

        columnHelper.accessor("action", {
            header: t("Action"),
            cell: (info) => (
                <ActionButtons
                    actions={{
                        // view: {
                        //     show: checkPermission?.view,
                        //     action: () =>
                        //         navigate(
                        //             `/account-management/view-transaction/${info.row.original?.id}?type=details&isuser=true&iscreditor=true`
                        //         ),
                        // },
                        edit: {
                            show: checkPermission?.update,
                            action: () =>
                                navigate(
                                    info.row.original?.from_user_id &&
                                        info.row.original?.to_user_id
                                        ? `/bank_tobank_transfer/${info.row.original?.id}?type=UU`
                                        : `/bank_tobank_transfer/${info.row.original?.id}`
                                ),
                        },
                        // delete: {
                        //     show: checkPermission?.delete,
                        //     action: () => {
                        //         setIdToDelete(`${info.row.original?.id}`);
                        //         setShowAlert(true);
                        //         setTransactiontype(info.row.original?.txn_category);
                        //     },
                        // },
                    }}
                />
            ),
        }),
    ];
    const fetchallbankTransactions = async () => {
         setIsLoading(true);
        const params = {
            pageNo,
            pageSize,
            search,
            user_id: user_id,
            user_type: user_type,
        };
        const res = await getallbanktobankTransaction(params);
        if (res?.status) {
            // const data = res.data.filter((item) => item.account_number);
            setRows(res?.data);

            setTotalData(res?.pageDetails?.total);
        } else {
            setRows([]);
            setTotalData(0);
        }
        setIsLoading(false);
    };

    const handleDelete = async () => {
        const body = {
            id: idToDelete,
            user_id: user_id,
            user_type: user_type,
            transaction_type: transactionType,
        };

        const res = await deleteBanktoBank(body);
        if (res?.status) {
            toast.success(res?.message);
            setRows((prev) => prev.filter((itm) => itm.id !== +idToDelete));
            fetchallbankTransactions();
        } else {
            toast.error(res?.message);
        }
        setIdToDelete("");
        setShowAlert(false);
    };

    useEffect(() => {
        fetchallbankTransactions();
    }, [search, pageNo, pageSize]);

    return (
        <>
            <Helmet>
                <title>Fund Transfer · CMS Electricals</title>
            </Helmet>
            <Col md={12} data-aos={"fade-up"}>
                <Tabs
                    onClick={(e, tab) => handleClick(e, tab)}
                    activeTab={activeTab}
                    ulClassName="border-primary p-2 border-bottom"
                    activityClassName="bg-secondary"
                >
                    <Tab className="pe-none fs-15 fw-bold" />
                    <Tab className={`ms-auto ${inner.cls("bank-to-bank-transfer")}`} title={[t("Bank to Bank Transfer")]}>
                        {activeTab == "2" && canSeeInner("bank-to-bank-transfer") && (
                            <>
                                {/* <DynamicFilter fields={fields} onChange={handleFilterChange} /> */}
                                <CustomTable
                                    id={"fund_transfer"}
                                    userPermission={checkPermission}
                                    isLoading={isLoading}
                                    rows={rows || []}
                                    columns={columns}
                                    pagination={{
                                        pageNo,
                                        pageSize,
                                        totalData,
                                    }}
                                    customHeaderComponent={() => (
                                        <TableHeader
                                            userPermission={{ ...checkPermission, create: hasFlag("bank-to-bank-transfer", "create", checkPermission?.create) }}
                                            setSearchText={setSearch}
                                            button={{
                                                noDrop: true,
                                                to: `/bank_tobank_transfer/new`,
                                                title: "Create",
                                            }}
                                        />
                                    )}
                                    tableTitleComponent={
                                        <div>
                                            <UserPlus /> <strong>Bank Accounts</strong>
                                        </div>
                                    }
                                />
                                <ConfirmAlert
                                    size={"sm"}
                                    deleteFunction={handleDelete}
                                    hide={setShowAlert}
                                    show={showAlert}
                                    title={"Confirm Delete"}
                                    description={"Are you sure you want to delete this!!"}
                                />
                            </>
                        )}
                    </Tab>
                    <Tab className={inner.cls("bank-cash-withdraw-deposit")} title={t("Cash Withdraw/Deposit")}>
                        {activeTab == "3" && canSeeInner("bank-cash-withdraw-deposit") && (
                            <CashDepositwithdraw checkPermission={{ ...checkPermission, create: hasFlag("bank-cash-withdraw-deposit", "create", checkPermission?.create) }} />
                        )}
                    </Tab>
                    <Tab className={inner.cls("bank-cash-voucher")} title={t("Cash Voucher")}>
                        {activeTab == "4" && canSeeInner("bank-cash-voucher") && (
                            <CashVoucher checkPermission={{ ...checkPermission, create: hasFlag("bank-cash-voucher", "create", checkPermission?.create) }} />
                        )}
                    </Tab>
                </Tabs>
            </Col>
        </>
    );
};

export default Transaction;
